/*
	password_strength.js — password strength checker using @zxcvbn-ts/core.

	Returns { score: 0-4, label, color, tips } where:
		0 = Weak, 1 = Fair, 2 = Good, 3 = Strong, 4 = Very Strong

	zxcvbn is a realistic password strength estimator (Dropbox) that detects
	dictionary words, common patterns, keyboard walks, repeats, l33t, dates,
	and more — far better than raw charset math. Tips come from zxcvbn's own
	feedback (via @zxcvbn-ts/language-en).

	score <= 1 is considered "weak" and triggers the weak-password banner.

	Advisory-only — NEVER blocks submission.
*/

import { ZxcvbnFactory } from "@zxcvbn-ts/core"
import {
	translations as enTranslations,
	dictionary as enDictionary,
} from "@zxcvbn-ts/language-en"
import {
	translations as frTranslations,
	dictionary as frDictionary,
} from "@zxcvbn-ts/language-fr"

// Merge EN + FR dictionaries so both language words are detected.
// Keys are namespaced ("-en" / "-fr") so they don't collide.
const mergedDictionary = { ...enDictionary, ...frDictionary }

const zxcvbn = new ZxcvbnFactory({
	translations: enTranslations, // UI strings; EN is fine for the app
	dictionary: mergedDictionary,
})

const LABELS = ["Weak", "Fair", "Good", "Strong", "Very Strong"]
const COLORS = ["#d2691e", "#d2691e", "#ffc107", "#8bc34a", "#4caf50"]

/**
 * Check password strength. Advisory-only — never blocks.
 *
 * @param {string} password
 * @returns {{score: number, label: string, color: string, tips: string[]}}
 *   score: 0-4 (0=Weak, 1=Fair, 2=Good, 3=Strong, 4=Very Strong)
 *   tips: 0-2 short specific suggestions from zxcvbn
 */
export function checkPasswordStrength(password) {
	if (!password || password.length === 0) {
		return { score: 0, label: "Empty", color: "#d2691e", tips: [] }
	}

	const result = zxcvbn.check(password)
	const score = result.score

	// Build tips from zxcvbn's feedback (warning + suggestions, max 2).
	const tips = []
	if (result.feedback) {
		if (result.feedback.warning) tips.push(result.feedback.warning)
		if (result.feedback.suggestions) {
			for (const s of result.feedback.suggestions) {
				if (tips.length >= 2) break
				tips.push(s)
			}
		}
	}

	return {
		score,
		label: LABELS[score] || "Weak",
		color: COLORS[score] || "#d2691e",
		tips: tips.slice(0, 2),
	}
}

/**
 * Returns true if the password is considered weak (score <= 1).
 * Used by the load-time banner logic to decide whether to warn.
 */
export function isWeakPassword(password) {
	return checkPasswordStrength(password).score <= 1
}
